import viewpaths from "global/constants/ViewPathConstants";
import TOUR_IDS from "../tour-ids";
import contactsSteps from "../steps/contacts.steps";
import dashboardSteps from "../steps/dashboard.steps";
import insightsSteps from "../steps/insights.steps";
import reportsSteps from "../steps/reports.steps";
import requestsSteps from "../steps/requests.steps";
import inboxSteps from "../steps/inbox.steps";
import templatesSteps from "../steps/templates.steps";
import viewRequestSteps from "../steps/view-request.steps";
import webFormsSteps from "../steps/web-forms.steps";
import type { TourDefinition } from "../../types/tour.types";

const firstVisitTour = (
  tour: Pick<
    TourDefinition,
    | "id"
    | "name"
    | "description"
    | "route"
    | "steps"
    | "welcome"
    | "complete"
    | "autoStartWhen"
  >,
): TourDefinition => ({
  ...tour,
  version: 1,
  autoStart: true,
});

export const dashboardTour = firstVisitTour({
  id: TOUR_IDS.DASHBOARD,
  name: "Dashboard tour",
  description: "Where to send from, and what each panel is telling you.",
  route: viewpaths.dashboardViewPath,
  autoStartWhen: "dashboard.dropzone",
  steps: dashboardSteps,
  welcome: {
    title: "Welcome to InkFree!",
    body: "Take a quick tour to learn how to create sign requests, manage templates, and track your documents.",
    primaryLabel: "Start Tour",
    secondaryLabel: "Maybe Later",
    footnote: "About 2 minutes · {steps} steps",
  },
  complete: {
    title: "You're all set!",
    body: "You've completed the tour. Start creating sign requests or explore the dashboard at your own pace.",
    primaryLabel: "Create First Request",
    primaryRoute: `${viewpaths.createSignRequestsViewPath}?type=others`,
    secondaryLabel: "Explore on my own",
    // Where TourLauncher actually lives — the top bar's Help & support panel.
    footnote: "You can restart this tour any time from Help & support",
  },
});

export const templatesTour = firstVisitTour({
  id: TOUR_IDS.TEMPLATES,
  name: "Templates tour",
  description: "Build a template once, then send from it in two clicks.",
  route: viewpaths.templatesViewPath,
  autoStartWhen: "templates.ready",
  steps: templatesSteps,
});

export const requestsTour = firstVisitTour({
  id: TOUR_IDS.REQUESTS,
  name: "Sign requests tour",
  description: "Organise, find and follow up on everything you've sent.",
  route: viewpaths.sentSignRequestsViewPath,
  autoStartWhen: "requests.list",
  steps: requestsSteps,
});

export const viewRequestTour = firstVisitTour({
  id: TOUR_IDS.VIEW_REQUEST,
  name: "Request details tour",
  description: "Track one request, chase it, and read its audit trail.",
  route: `${viewpaths.viewRequestViewPath}/:id`,
  autoStartWhen: "viewRequest.documents",
  steps: viewRequestSteps,
});

export const inboxTour = firstVisitTour({
  id: TOUR_IDS.INBOX,
  name: "Inbox tour",
  description: "What others have sent you, and what to do with each one.",
  route: viewpaths.inboxViewPath,
  autoStartWhen: "inbox.list",
  steps: inboxSteps,
});

export const contactsTour = firstVisitTour({
  id: TOUR_IDS.CONTACTS,
  name: "Contacts tour",
  description: "Keep an address book so recipients fill themselves in.",
  route: viewpaths.contactsViewPath,
  autoStartWhen: "contacts.ready",
  steps: contactsSteps,
});

export const webFormsTour = firstVisitTour({
  id: TOUR_IDS.WEB_FORMS,
  name: "Web forms tour",
  description: "Turn a template into a link anyone can open and sign.",
  route: viewpaths.webFormsViewPath,
  autoStartWhen: "webforms.list",
  steps: webFormsSteps,
});

export const insightsTour = firstVisitTour({
  id: TOUR_IDS.INSIGHTS,
  name: "Insights tour",
  description: "Read the charts: where requests move, and where they stall.",
  route: viewpaths.insightsViewPath,
  autoStartWhen: "insights.chart",
  steps: insightsSteps,
});

export const reportsTour = firstVisitTour({
  id: TOUR_IDS.REPORTS,
  name: "Analytics tour",
  description: "The four detailed breakdowns behind the charts.",
  route: viewpaths.reports,
  autoStartWhen: "reports.tabs",
  steps: reportsSteps,
});

export const PAGE_TOURS: TourDefinition[] = [
  dashboardTour,
  templatesTour,
  requestsTour,
  viewRequestTour,
  inboxTour,
  contactsTour,
  webFormsTour,
  insightsTour,
  reportsTour,
];

export default PAGE_TOURS;
